import { animate, animateChild, group, query, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, DatePipe, DecimalPipe, NgClass, NgTemplateOutlet, getCurrencySymbol } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  CurrencyService,
  DeleteLocalCacheCategory,
  ENVIRONMENT,
  ModalService,
  SharedService,
  UnitsConversionHelper,
  UserService
} from '@cfp/core';
import { filterMeasureSearchFacetsByHeatingType } from '@cfp/features-shared/building-measures/filter-measure-search-facets-by-heating-type';
import { SubsidyMeasureIcon, SubsidyMeasureLegend, SubsidyService } from '@cfp/features-shared/subsidy';
import {
  DataTableColumn,
  DataTableComponent,
  DataTableSortOrder,
  DeprecatedDataTableModule
} from '@cfp/shared/deprecated/data-table';
import { BuildingMeasuresOverviewExcelExportService } from '@cfp/shared/energy';
import { RoadmapService } from '@cfp/shared/roadmap';
import { ROADMAP_IMPLEMENTATION_YEARS_PROPERTY_END } from '@cfp/shared/roadmap/const';
import {
  AriaLabelPipe,
  Badge,
  BreakpointObserver,
  BreakpointState,
  Button,
  ButtonToggle,
  Co2Module,
  CoalUnitsConversionPipe,
  CurrencyModule,
  CurrencySymbolPipe,
  Dropdown,
  ExcelExportService,
  ExcelExportSheet,
  GasUnitsConversionPipe,
  HeatUnitsConversionPipe,
  Icon,
  InputRadio,
  Link,
  LiveRegionStatusService,
  LiveRegionStatusUpdaterModule,
  MeasureStatusDropdown,
  OilUnitsConversionPipe,
  PaybackPeriodModule,
  PelletUnitsConversionPipe,
  ProgressBar,
  Select,
  SelectOption,
  SelectYear,
  TextWrapWithContent,
  WoodUnitsConversionPipe
} from '@cfp/ui';
import { LoadingSpinner, TranslatedRouterLink } from '@cfp/ui/common';
import { AlertModalResult, AlertService, InfoModalButton } from '@cfp/ui/feedback';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BuildingDetailsDTO,
  BuildingMeasureDTO,
  BuildingMeasureSearchFacetsDTO,
  BuildingMeasureTableItemDTO,
  BuildingMeasureTypeDTO,
  BuildingMeasuresListService,
  BuildingsService,
  CurrencyCodeDTO,
  HeatingConsumptionTypeDTO,
  LabelGroupDTO,
  MeasureCodeDTO,
  MeasureListDTO,
  MeasureStatusDTO
} from 'csr-asset-app';
import { EnergyUnitPreferenceDTO, LicenseDTO } from 'csr-login-app';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { Observable, map, mergeMap, of, skip, tap } from 'rxjs';
import { IMPLEMENTATION_ORDER } from './building-measures-implementation-order';
import { BuildingMeasuresOverviewHeaderInfo } from './building-measures-overview-header-info';
import { BuildingMeasuresOverviewMeasure } from './building-measures-overview-measure';
import { BuildingMeasuresOverviewObjective } from './building-measures-overview-objective';
import { BuildingMeasuresService } from './building-measures-service';
// eslint-disable-next-line import/no-restricted-paths -- Measure side panel is in domains and is coupled to roadmap, we should investigate how we can move it to features-shared
import { BuildingMeasureSidePanel } from '@cfp/domains/measure/side-panel';
import {
  BuildingFilterBadgeService,
  BuildingMeasuresFiltersSidePanel,
  FilterBadge,
  FiltersBuildingSearchService
} from '@cfp/shared/building/filters';
import { MeasureUpdateModal } from '@cfp/shared/measure/update-modal';
import { LabelGroupModule, LabelGroupService } from '@cfp/shared/ui/label-group';
import {
  MeasurementArea,
  MeasurementCoalPerSquare,
  MeasurementCostPerSquare,
  MeasurementGasPerSquare,
  MeasurementHeatPerSquare,
  MeasurementKwhPerSquare,
  MeasurementLiterPerSquare,
  MeasurementMassPerSquare,
  MeasurementPelletPerSquare,
  MeasurementType,
  MeasurementWoodPerSquare
} from '@cfp/shared/ui/measurement';
import { LabelImpactBarPipe } from '@cfp/ui/pipes/label-impact-bar';
import { BuildingScenariosButton } from '../building_scenarios/building_scenarios-button/building-scenarios-button';

@Component({
  selector: 'cfp-building-measures-overview',
  templateUrl: './building-measures-overview.html',
  styleUrls: ['./building-measures-overview.scss'],
  animations: [
    trigger('slideWaitAction', [
      transition('* <=> *', [group([query('@slideAction', animateChild()), animate('350ms')])])
    ]),
    trigger('slideAction', [
      transition(':enter', [style({ maxHeight: 0 }), animate('350ms', style({ maxHeight: '170px' }))]),
      transition(':leave', [style({ maxHeight: '170px' }), animate('350ms', style({ maxHeight: 0 }))])
    ])
  ],
  imports: [
    ButtonToggle,
    LiveRegionStatusUpdaterModule,
    TranslateModule,
    ReactiveFormsModule,
    Button,
    DeprecatedDataTableModule,
    FilterBadge,
    NgClass,
    FormsModule,
    Dropdown,
    MeasurementArea,
    LabelGroupModule,
    InputRadio,
    Icon,
    TextWrapWithContent,
    MeasurementKwhPerSquare,
    MeasurementGasPerSquare,
    MeasurementHeatPerSquare,
    DecimalPipe,
    MeasurementCostPerSquare,
    CurrencyModule,
    Badge,
    PaybackPeriodModule,
    TooltipModule,
    TranslatedRouterLink,
    LoadingSpinner,
    InfoModalButton,
    MeasurementLiterPerSquare,
    MeasurementWoodPerSquare,
    MeasurementCoalPerSquare,
    MeasurementPelletPerSquare,
    GasUnitsConversionPipe,
    Co2Module,
    MeasurementMassPerSquare,
    ProgressBar,
    LabelImpactBarPipe,
    NgTemplateOutlet,
    MeasureStatusDropdown,
    Select,
    SelectOption,
    SelectYear,
    SubsidyMeasureIcon,
    SubsidyMeasureLegend,
    Link,
    AriaLabelPipe,
    AsyncPipe,
    HeatUnitsConversionPipe,
    OilUnitsConversionPipe,
    WoodUnitsConversionPipe,
    PelletUnitsConversionPipe,
    CoalUnitsConversionPipe,
    AsyncPipe,
    BuildingScenariosButton
  ],
  providers: [BuildingMeasuresOverviewExcelExportService, ExcelExportService, CurrencySymbolPipe, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildingMeasuresOverview {
  protected readonly environment = inject(ENVIRONMENT);

  public readonly building = input.required<BuildingDetailsDTO | undefined>();
  public readonly portfolioId = input<number | undefined>(undefined);
  public readonly buildingId = input<number | undefined>(undefined);
  public readonly buildingPublicSharedId = input<string | undefined>(undefined);
  public readonly showTitle = input<boolean>(true);
  public readonly titleClass = input<string>();
  public readonly showBody = input<boolean>(false);
  public readonly showInfo = input<boolean>(true);
  public readonly showStatus = input<boolean>(true);
  public readonly isPdfServiceRequest = input<boolean>(false); // This field is used to hide the status column in the measures table when generating a PDF.
  public readonly showInvestor = model<boolean>(true);
  public readonly showEnergyReduction = model<boolean>(false);
  public readonly showEnergyUsage = input<boolean>(false);
  public readonly showCostSavings = input<boolean>(true);
  public readonly showCo2ConsumptionReduction = input<boolean>(true);
  public readonly showInPackage = input<boolean>(false);
  public readonly showSeeDetailsLink = input<boolean>(false);
  public readonly showSubsidyIcon = input<boolean>(false);
  public readonly showScenarios = input<boolean>(true);
  public readonly showImplementationYear = input<boolean>(true);
  public readonly showLegend = input<boolean>(true);
  public readonly showIcon = input<boolean>(true);
  public readonly showVat = input<boolean>(true);
  public readonly canExport = input<boolean>(false);
  public readonly defaultScenario = input<string | undefined>(undefined);
  public readonly canEditMeasure = input<boolean>(true);
  public readonly impactEnergyLabelAsProgress = input<boolean>(false);
  public readonly showAll = input<boolean>(true);
  public readonly showToolbar = input<boolean>(true);
  public readonly showPaybackPeriodInYearsOnly = input<boolean>(false);
  public readonly hideNonCostReducingMeasures = input<boolean>(false);
  public readonly visibleNonCostReducingMeasureCodes = input<MeasureCodeDTO[] | undefined>(undefined);
  public readonly headerAriaDescribedby = input<
    | {
        [key: string]: string;
      }
    | undefined
  >(undefined);
  public readonly tableAriaLabelledby = input<string | undefined>(undefined);
  public readonly useStickyColumns = input<boolean>(false);
  public readonly canSortByHeaders = input<boolean>(true);

  public readonly allMeasuresButtonStyle = input<string>('secondary');
  public readonly showAddButton = input<boolean>(true);
  public readonly rememberStateId = input<string | undefined>(undefined);
  public readonly tableClass = input<string>('table-striped table-hover');
  public readonly headerInfo = input<BuildingMeasuresOverviewHeaderInfo[] | undefined>(undefined);
  public readonly measureList = input<MeasureListDTO | undefined>(undefined);
  public readonly energyLabelTranslationPrefix = input<string>('');
  public readonly labelGroups = input<LabelGroupDTO[] | undefined>(undefined);
  public readonly labelGroup = model<LabelGroupDTO | undefined>(undefined);
  public readonly loading = model<boolean>(false);
  public readonly module = input<string>('energy');
  public readonly showOptions = input<boolean>(true);
  public readonly showMeasureDetailsButtonSidePanel = input<boolean>(
    this.environment.components.showMeasureDetailsButtonSidePanel ?? true
  );
  public readonly objective = model<BuildingMeasuresOverviewObjective | undefined>(undefined);
  public readonly measureParametersWithInfo = input<string[] | undefined>(undefined);
  public readonly measureParametersToHide = input<string[] | undefined>(undefined);
  public readonly enableShowAsCard = input<boolean>(false);
  public readonly measuresWithBadge = input<Record<MeasureCodeDTO, string> | undefined>(undefined);

  public readonly measureCount = output<number>();
  public readonly openInfoModal = output<MeasureCodeDTO>();

  public readonly filteredBuildingMeasures = signal<BuildingMeasuresOverviewMeasure[] | undefined>(undefined);
  public readonly currencyCode = signal<CurrencyCodeDTO>('EUR' as CurrencyCodeDTO);

  protected readonly MeasurementType = MeasurementType;
  protected readonly LabelGroupDTO = LabelGroupDTO;
  protected readonly MeasureStatusDTO = MeasureStatusDTO;
  protected readonly BuildingMeasureTypeDTO = BuildingMeasureTypeDTO;
  protected readonly BuildingMeasuresOverviewObjective = BuildingMeasuresOverviewObjective;

  protected readonly totalFilteredMeasures = signal<number>(0);
  protected readonly hasError = signal<boolean>(false);
  protected readonly initialLoading = signal<boolean>(true);
  protected readonly columns = computed<DataTableColumn[]>(() => {
    const showColumns = this.showColumns();
    const maxColumns = 10;
    let numOfColumns = 5;
    numOfColumns += this.showInvestor() ? 1 : 0;
    numOfColumns += this.showCo2ConsumptionReduction() ? 1 : 0;
    if (!this.isPdfServiceRequest()) {
      numOfColumns += this.showStatus() ? 1 : 0;
    }
    numOfColumns += this.showInPackage() ? 1 : 0;
    numOfColumns += this.showSeeDetailsLink() ? 1 : 0;

    const cols: DataTableColumn[] = [
      { name: 'name', title: 'measure', width: Math.floor((20 / numOfColumns) * maxColumns) + '%' }
    ];

    if (showColumns.energyUsage) {
      cols.push({
        name: 'energyConsumptionReduction',
        title: 'energyConsumptionReduction',
        width: Math.floor((9 / numOfColumns) * maxColumns) + '%',
        defaultSortOrder: DataTableSortOrder.Descending,
        onCompare: (
          a: BuildingMeasuresOverviewMeasure,
          b: BuildingMeasuresOverviewMeasure,
          sortOrder: DataTableSortOrder | DataTableSortOrder[]
        ) => this.onEnergyConsumptionCompare(a, b, sortOrder)
      });
    }

    if (showColumns.costReduction) {
      let investmentColumn: DataTableColumn = {
        name: 'investment',
        title: 'investment',
        width: Math.floor((7 / numOfColumns) * maxColumns) + '%'
      };

      if (this.licenses().vat) {
        investmentColumn = {
          ...investmentColumn,
          infoTitle: 'vat.measuresTableHeaderInfoIcon.title',
          infoText: 'vat.measuresTableHeaderInfoIcon.infoText'
        };
      }

      cols.push({
        name: 'costReduction',
        title: 'costReduction',
        width: Math.floor((7 / numOfColumns) * maxColumns) + '%',
        defaultSortOrder: DataTableSortOrder.Descending
      });
      cols.push(investmentColumn);
      cols.push({
        name: 'paybackPeriodYears',
        title: 'tvtYear',
        width: Math.floor((10 / numOfColumns) * maxColumns) + '%',
        onCompare: (
          a: BuildingMeasuresOverviewMeasure,
          b: BuildingMeasuresOverviewMeasure,
          sortOrder: DataTableSortOrder | DataTableSortOrder[]
        ) => this.onPaybackPeriodYearsCompare(a, b, sortOrder)
      });
    }

    if (showColumns.energyReduction) {
      cols.push({
        name: 'energyConsumptionReduction',
        title: 'energyConsumptionReduction',
        width: Math.floor((7 / numOfColumns) * maxColumns) + '%',
        defaultSortOrder: DataTableSortOrder.Descending,
        onCompare: (
          a: BuildingMeasuresOverviewMeasure,
          b: BuildingMeasuresOverviewMeasure,
          sortOrder: DataTableSortOrder | DataTableSortOrder[]
        ) => this.onEnergyConsumptionCompare(a, b, sortOrder)
      });
    }

    if (showColumns.investor) {
      cols.push({
        name: 'investor',
        title: 'investor',
        width: Math.floor((7 / numOfColumns) * maxColumns) + '%'
      });
    }

    if (showColumns.co2) {
      cols.push({
        name: 'co2ConsumptionReduction',
        title: 'reductionPotential',
        width: Math.floor((7 / numOfColumns) * maxColumns) + '%',
        defaultSortOrder: DataTableSortOrder.Descending
      });
    }

    if (showColumns.impactEnergyLabel) {
      const impactEnergyLabelTitleKey = this.getEnergyLabelTranslationKey('impactEnergyLabel');
      let impactEnergyLabelTitle = this.translateService.instant(impactEnergyLabelTitleKey, {
        labelGroup: this.translateService.instant(`labelGroup.${this.labelGroup()}`)
      });
      if (impactEnergyLabelTitle === impactEnergyLabelTitleKey) {
        impactEnergyLabelTitle = this.translateService.instant('impactEnergyLabel', {
          labelGroup: this.translateService.instant(`labelGroup.${this.labelGroup()}`)
        });
      }

      cols.push({
        name: 'impactEnergyLabel',
        title: impactEnergyLabelTitle,
        width: Math.floor((10 / numOfColumns) * maxColumns) + '%',
        defaultSortOrder: DataTableSortOrder.Descending,
        onCompare: (
          a: BuildingMeasuresOverviewMeasure,
          b: BuildingMeasuresOverviewMeasure,
          sortOrder: DataTableSortOrder | DataTableSortOrder[]
        ) => this.onImpactEnergyLabelCompare(a, b, sortOrder)
      });
    }

    if (showColumns.status) {
      cols.push({
        name: 'status',
        title: 'status',
        width: Math.floor((10 / numOfColumns) * maxColumns) + '%',
        defaultSortOrder: DataTableSortOrder.Descending
      });
    }

    if (showColumns.implementationYear) {
      cols.push({
        name: 'implementationYear',
        title: 'implementationYear',
        width: Math.floor((10 / numOfColumns) * maxColumns) + '%'
      });
    }

    if (showColumns.inPackage) {
      cols.push({
        name: 'status',
        title: 'inPackage',
        width: Math.floor((13 / numOfColumns) * maxColumns) + '%'
      });
    }

    if (showColumns.seeDetailsLink) {
      cols.push({
        name: 'seeDetailsLink',
        title: 'measures.action',
        width: Math.floor((10 / numOfColumns) * maxColumns) + '%',
        noSort: true
      });
    }

    const headerAriaDescribedby = this.headerAriaDescribedby();
    if (headerAriaDescribedby) {
      Object.keys(headerAriaDescribedby).forEach((key) => {
        const column = cols.find((column) => column.name === key);
        if (column) {
          column.ariaDescribedby = headerAriaDescribedby[key];
        }
      });
    }

    if (!this.canSortByHeaders()) {
      cols.forEach((column: DataTableColumn) => (column.noSort = true));
    }

    const info = this.headerInfo();
    if (info) {
      cols.forEach((column: DataTableColumn) => {
        const match = info.find((h: BuildingMeasuresOverviewHeaderInfo) => h.columnNames.includes(column.name));
        if (match) {
          column.infoTitle = match.infoTitle;
          column.infoText = match.infoText;
          column.infoClass = match.iconClass;
        }
      });
    }

    return cols;
  });
  protected readonly filtersActive = signal<boolean>(false);
  protected readonly heatingConsumptionType = signal<HeatingConsumptionTypeDTO | undefined>(undefined);
  protected readonly buildingMeasures = signal<BuildingMeasuresOverviewMeasure[] | undefined>(undefined);
  protected readonly showOnlyDoneMeasures = computed(() => !this.showScenarios() && this.defaultScenario() === 'done');

  protected readonly showColumns = computed(() => {
    const showOnlyDone = this.showOnlyDoneMeasures();
    return {
      energyUsage: this.showEnergyUsage(),
      costReduction: !showOnlyDone,
      investment: !showOnlyDone,
      paybackPeriod: !showOnlyDone,
      energyReduction: !showOnlyDone && this.showEnergyReduction(),
      investor: !showOnlyDone && this.showInvestor(),
      co2: this.showCo2ConsumptionReduction(),
      impactEnergyLabel: !showOnlyDone,
      status: this.showStatus() && !this.isPdfServiceRequest(),
      implementationYear: this.showImplementationYear() && !!this.licenses().roadmap,
      inPackage: this.showInPackage(),
      seeDetailsLink: this.showSeeDetailsLink()
    };
  });

  protected readonly plannedMeasuresCount = computed(
    () => this.buildingMeasures()?.filter((m) => m.status === MeasureStatusDTO.PLANNED).length || 0
  );

  protected readonly hasCustomerMeasures = computed(() => {
    const codes = this.environment.components.customerMeasures;
    if (!codes) return false;
    return this.buildingMeasures()?.some((m) => m.code && codes.includes(m.code)) || false;
  });

  protected readonly hasRecognizedMeasuresVisible = computed(
    () => this.filteredBuildingMeasures()?.some((m) => m.recognized) || false
  );

  protected readonly hasSubsidizedMeasuresVisible = computed(
    () => this.filteredBuildingMeasures()?.some((m) => m.subsidyAvailable) || false
  );

  protected readonly hasUserDefinedMeasuresVisible = computed(
    () =>
      this.filteredBuildingMeasures()?.some((m) => m.buildingMeasureType === BuildingMeasureTypeDTO.USERDEFINED) ||
      false
  );

  protected readonly hasCustomerMeasuresVisible = computed(() => {
    const codes = this.environment.components.customerMeasures;
    if (!codes) return false;
    return this.filteredBuildingMeasures()?.some((m) => !!m.code && codes.includes(m.code)) || false;
  });

  protected readonly canShowAll = computed(
    () =>
      (this.filteredBuildingMeasures()?.length || 0) < (this.buildingMeasures()?.length || 0) ||
      this.activeScenario() === 'fiveYear'
  );
  protected readonly sortBy = signal<string | string[] | undefined>(undefined);
  protected readonly sortOrder = signal<DataTableSortOrder | DataTableSortOrder[] | undefined>(undefined);
  protected readonly searchText = signal<string | undefined>(undefined);
  protected readonly showM2 = signal<boolean>(false);
  protected readonly canUpdateMeasureStatus = signal<boolean>(false);
  protected readonly showAsCard = signal<boolean>(false);
  protected readonly showTable = signal<boolean>(true);
  protected readonly canSeeBudgeting = signal<boolean>(false);
  protected readonly filterBadges = signal<{ value: any; display: string }[]>([]);
  protected readonly showEnergyIcon = signal<boolean>(false);
  protected readonly mainLabelGroup = signal<LabelGroupDTO | undefined>(undefined);
  protected readonly openMeasureCode = signal<string | undefined>(undefined);

  private readonly hasEpcScenariosPlannerLicense = signal<boolean>(false);
  private readonly activeScenario = signal<string>('allMeasures');
  private buildingMeasuresFiltersSidePanelModalRef: BsModalRef<BuildingMeasuresFiltersSidePanel>;

  protected readonly filtersBuildingSearchService = inject(FiltersBuildingSearchService);
  protected readonly buildingFilterBadgeService = inject(BuildingFilterBadgeService);
  protected readonly unitsConversionHelper = inject(UnitsConversionHelper);
  protected readonly currencyService = inject(CurrencyService);

  private readonly destroyRef = inject(DestroyRef);
  private readonly buildingsService = inject(BuildingsService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly roadmapService = inject(RoadmapService);
  private readonly userService = inject(UserService);
  private readonly sharedService = inject(SharedService);
  private readonly buildingMeasuresService = inject(BuildingMeasuresService);
  private readonly buildingMeasuresListService = inject(BuildingMeasuresListService);
  private readonly labelGroupService = inject(LabelGroupService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly modalService = inject(ModalService);
  private readonly buildingMeasuresOverviewExcelExportService = inject(BuildingMeasuresOverviewExcelExportService);
  private readonly liveRegionStatusService = inject(LiveRegionStatusService);
  private readonly subsidyService = inject(SubsidyService);

  protected readonly hasSubsidyLicense = toSignal(this.userService.hasLicense('subsidy'));
  protected readonly startYear = this.roadmapService.getStartYear();
  protected readonly endYear = ROADMAP_IMPLEMENTATION_YEARS_PROPERTY_END;
  protected readonly showCalculatedValueIcon = this.environment.components.showCalculatedValueIcon === true;

  protected readonly showM2InDropdown = signal<boolean>(false);
  protected readonly canResetMeasures = signal<boolean>(false);
  protected readonly licenses = signal<Partial<Record<LicenseDTO.LicenseTypeEnum, boolean>>>({});
  protected readonly hasBuildingScenariosLicense = computed(
    () => this.licenses()?.[LicenseDTO.LicenseTypeEnum.BuildingScenarios] === true
  );

  protected getTypedMeasure(measure: BuildingMeasuresOverviewMeasure): BuildingMeasuresOverviewMeasure {
    return measure;
  }

  protected readonly showOptionsDropdown = computed(() => {
    const showOptions = this.showOptions();
    const labelGroups = this.labelGroups();
    if (!showOptions || !labelGroups) {
      return false;
    }

    return (
      labelGroups.length > 1 ||
      this.showM2InDropdown() ||
      this.canResetMeasures() ||
      (this.canExport() && this.licenses().export_to_excel)
    );
  });

  protected shouldShowSubsidyIcon(measureCode: MeasureCodeDTO): boolean {
    return this.subsidyService.hasMeasureSubsidy(measureCode);
  }

  private readonly dataTable = viewChild<DataTableComponent>('dataTable');

  private keepCurrentSorting = false;

  constructor() {
    this.resetFiltersAndBadges();
    if (this.environment.components.showInvestor === false) {
      this.showInvestor.set(false);
    }

    if (this.environment.components.showEnergyReductionInTable === true) {
      this.showEnergyReduction.set(true);
    }

    if (this.buildingPublicSharedId()) {
      this.licenses.set({});
    } else {
      this.userService
        .getLicenses(true)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (licenses) => {
            this.licenses.set(licenses);
            this.hasEpcScenariosPlannerLicense.set(!!this.licenses().epc_scenarios_planner);
            this.canSeeBudgeting.set(!!this.licenses().budget);
          },
          error: () => {
            this.loading.set(false);
            this.initialLoading.set(false);
            this.hasError.set(true);
          }
        });
    }

    if (this.canEditMeasure()) {
      this.canUpdateMeasureStatus.set(this.userService.hasScope('buildings-measures-modify'));
      this.canResetMeasures.set(this.userService.hasScope('buildings-measures-reset'));
    }
    this.showEnergyIcon.set(this.userService.user()?.energyUnitPreference == EnergyUnitPreferenceDTO.KWH);

    effect(() => {
      this.activeScenario.set(this.defaultScenario() || this.environment.components.defaultScenario || 'allMeasures');
    });

    this.sharedService.reloadComponentObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.keepCurrentSorting = true;
      this.getMeasures(false);
    });

    this.breakpointObserver
      .observe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state: BreakpointState) => {
        this.showM2InDropdown.set(state.breakpoints.xs === true || state.breakpoints.sm === true);
        const newShowAsCard = this.enableShowAsCard() ? state.breakpoints.xs === true : false;
        if (this.showAsCard() !== newShowAsCard) {
          this.showAsCard.set(newShowAsCard);
          this.showTable.set(false);
          queueMicrotask(() => this.showTable.set(true));
        }
      });

    this.filterBadges.set(this.buildingFilterBadgeService.filterBadges);

    this.buildingFilterBadgeService
      .getFilterBadgesObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((badges) => {
        this.filterBadges.set(badges);
      });

    this.buildingFilterBadgeService
      .removedBadgeObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((badges) => {
        if (badges.length < 1) {
          this.filtersBuildingSearchService.searchFilters.set(undefined);
          this.filtersBuildingSearchService.searchFacets.set(undefined);
        }
        if (this.buildingMeasuresFiltersSidePanelModalRef?.content) {
          this.buildingMeasuresFiltersSidePanelModalRef.content.isLoading.set(true);
        }

        const searchFilters = this.filtersBuildingSearchService.searchFilters() ?? {};
        this.filtersBuildingSearchService.searchFilters.set(
          Object.entries(searchFilters)
            .filter((field) => {
              const fieldCopy = { ...field };
              delete fieldCopy['display'];

              return (
                badges.findIndex((badge) => {
                  return badge.value === Object.values(fieldCopy)[1];
                }) > -1
              );
            })
            .reduce((newSearchFilter, fieldValues) => {
              newSearchFilter[fieldValues[0]] = searchFilters[fieldValues[0]];
              return newSearchFilter;
            }, {})
        );

        this.getBuildingMeasures().subscribe();
      });

    // React to buildingId changes (no skip — input is undefined at construction time)
    toObservable(this.buildingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        if (id && id > 0 && !this.measureList()) {
          if (this.buildingMeasures()) {
            this.clearFilters();
          }
          this.getMeasures();
        }
      });

    // React to measureList input changes
    toObservable(this.measureList)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((list) => {
        if (list) {
          this.initMeasureList(list);
        }
      });

    // React to labelGroup changes
    toObservable(this.labelGroup)
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.filteredBuildingMeasures.update((measures) => (measures ? [...measures] : undefined));
        this.updateFilteredMeasures();
      });

    // React to objective changes
    toObservable(this.objective)
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((objective) => {
        this.changeObjective(objective);
      });
  }

  protected removeFilterBadge(index: number): void {
    this.buildingFilterBadgeService.removeBadge(index);
  }

  protected showFilters(): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }
    this.buildingMeasuresListService
      .v2BuildingsIdBuildingmeasuresFacetsPost(
        { ...this.filtersBuildingSearchService.searchFilters(), ...{ amountsPerM2: this.showM2() } },
        buildingId
      )
      .pipe(
        map((buildingMeasureSearchFacets: BuildingMeasureSearchFacetsDTO) => {
          const heatingConsumptionType = this.heatingConsumptionType();
          if (heatingConsumptionType) {
            filterMeasureSearchFacetsByHeatingType(buildingMeasureSearchFacets, heatingConsumptionType);
          }
          return buildingMeasureSearchFacets;
        })
      )
      .subscribe((buildingMeasureSearchFacets: BuildingMeasureSearchFacetsDTO) => {
        this.filtersBuildingSearchService.searchFacets.set(buildingMeasureSearchFacets);
        this.buildingMeasuresFiltersSidePanelModalRef = this.modalService.showSidePanel(
          BuildingMeasuresFiltersSidePanel
        );

        const modelRefContent = this.buildingMeasuresFiltersSidePanelModalRef.content;
        if (modelRefContent) {
          modelRefContent.totalFilteredMeasures.set(this.totalFilteredMeasures());
          modelRefContent.currencySymbol.set(
            getCurrencySymbol(this.currencyService.getUserCurrencyCode(this.currencyCode()), 'narrow')
          );
          modelRefContent.showM2.set(this.showM2());

          modelRefContent.applySearchFilters?.subscribe((searchFilters) => {
            modelRefContent.isLoading.set(true);
            this.filtersBuildingSearchService.searchFilters.set({ ...searchFilters });

            this.buildingMeasuresListService
              .v2BuildingsIdBuildingmeasuresFacetsPost(
                { ...this.filtersBuildingSearchService.searchFilters(), ...{ amountsPerM2: this.showM2() } },
                buildingId
              )
              .subscribe((buildingMeasureSearchFacets: BuildingMeasureSearchFacetsDTO) => {
                this.filtersBuildingSearchService.searchFacets.set(buildingMeasureSearchFacets);
                modelRefContent.ngOnInit();

                this.buildingMeasuresListService
                  .v2BuildingsIdBuildingmeasuresPost(
                    { ...this.filtersBuildingSearchService.searchFilters(), ...{ amountsPerM2: this.showM2() } },
                    buildingId
                  )
                  .subscribe((measureList: MeasureListDTO) => {
                    this.setMeasureList(measureList);
                  });
              });
          });

          this.buildingMeasuresFiltersSidePanelModalRef.onHidden?.subscribe(() => {
            this.filtersActive.set(this.filtersBuildingSearchService.hasSearchFilters());
            this.updateLiveRegionItemsShowing();
          });

          modelRefContent.clearFilters?.subscribe(() => {
            this.buildingFilterBadgeService.clearBadges();
            this.filtersBuildingSearchService.searchFilters.set(undefined);
            this.getBuildingMeasures().subscribe();
          });
        }
      });

    this.filtersActive.set(this.filtersBuildingSearchService.hasSearchFilters());
  }

  protected clearFilters(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.resetFiltersAndBadges();
    this.getBuildingMeasures().subscribe();
  }

  protected onRowClick(measure: BuildingMeasuresOverviewMeasure, index: number, rowElement: HTMLTableRowElement): void {
    this.goToMeasure(measure, index, rowElement?.querySelector('button'));
  }

  protected isScenarioActive(scenario: string): boolean {
    return this.activeScenario() === scenario;
  }

  protected getButtonClass(scenario: string): string {
    if (scenario === (this.environment.components.defaultScenario || 'allMeasures')) {
      return 'order-1';
    } else {
      return ' order-2';
    }
  }

  protected changeScenario(scenario: string): void {
    let needReloadMeasures = this.activeScenario() === 'fiveYear' || scenario === 'fiveYear';
    this.activeScenario.set(scenario);
    this.searchText.set(undefined);
    if (needReloadMeasures) {
      const subscription = this.measureCount.subscribe(() => {
        subscription.unsubscribe();
        this.updateLiveRegioContentUpdatedBelow();
        this.updateLiveRegionItemsShowing();
      });
      this.getMeasures();
    } else {
      this.updateFilteredMeasures();
      this.updateLiveRegioContentUpdatedBelow();
      this.updateLiveRegionItemsShowing();
    }
  }

  private updateLiveRegioContentUpdatedBelow(): void {
    this.liveRegionStatusService.updateStatus('liveAreaRegion.contentUpdatedBelow');
  }

  private updateLiveRegionItemsShowing(): void {
    const measures = this.filteredBuildingMeasures();
    this.liveRegionStatusService.updateStatus('items.showing', {
      numberOfItems: measures?.length || 0,
      singleOrPlural: this.translateService.instant(measures?.length === 1 ? 'measure' : 'measures')
    });
  }

  protected changeObjective(objective: BuildingMeasuresOverviewObjective | undefined): void {
    this.objective.set(objective);
    this.updateFilteredMeasures();
  }

  protected setShowM2(showM2: boolean): void {
    this.showM2.set(showM2);
    this.updateLiveRegioContentUpdatedBelow();
  }

  protected search(searchText: string): void {
    this.searchText.set(searchText);
    this.updateFilteredMeasures();

    this.updateLiveRegionItemsShowing();
  }

  protected saveStatus(buildingMeasure: BuildingMeasuresOverviewMeasure, newStatus: MeasureStatusDTO): void {
    const buildingId = this.buildingId();
    const portfolioId = this.portfolioId();
    if (!buildingId || !portfolioId) {
      return;
    }
    buildingMeasure.loading = true;
    const oldStatus = buildingMeasure.status ?? newStatus;
    buildingMeasure.status = newStatus;
    this.roadmapService
      .saveMeasureStatus(
        buildingMeasure as unknown as BuildingMeasureDTO,
        newStatus,
        oldStatus,
        portfolioId,
        buildingId
      )
      .subscribe({
        next: (isSaved: boolean) => {
          buildingMeasure.loading = false;

          buildingMeasure.status = isSaved ? newStatus : oldStatus;
          const existingBuildingMeasure = this.buildingMeasures()?.find((measure: BuildingMeasureTableItemDTO) => {
            return measure.id === buildingMeasure.id;
          });

          if (existingBuildingMeasure) {
            existingBuildingMeasure.status = buildingMeasure.status;
          }

          this.updateFilteredMeasuresResult();
        },
        error: () => {
          buildingMeasure.loading = false;
        }
      });
  }

  protected saveImplementationYear(buildingMeasure: BuildingMeasureTableItemDTO, implementationYear?: number): void {
    this.roadmapService
      .saveImplementationYear(this.roadmapService.mapToBuildingMeasureDetailsDTO(buildingMeasure), implementationYear)
      .subscribe();
  }

  protected addNewMeasure(): void {
    this.addOrEditUserDefiniedBuildingMeasure();
  }

  protected resetMeasuresToOutOfScope(): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }
    this.alertService
      .showConfirmation(
        'warning',
        'building.resetMeasures.outOfScope.confirmation.title',
        'building.resetMeasures.outOfScope.confirmation.text'
      )
      .subscribe({
        next: (result: AlertModalResult) => {
          if (result !== AlertModalResult.CONFIRM) {
            return;
          }

          this.loading.set(true);
          this.buildingsService.v2BuildingsBuildingIdMeasuresResetOutofscopePut(buildingId).subscribe({
            next: () => {
              this.sharedService.emitDeleteLocalCache(DeleteLocalCacheCategory.All);
              this.sharedService.emitReloadComponent();
              this.alertService.showAlert('success', 'building.resetMeasures.outOfScope.success.title');
            },
            error: () => {
              this.loading.set(false);
              this.alertService.showAlert('error', 'error.general');
            }
          });
        }
      });
  }

  protected resetMeasuresToDefault(): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }
    this.alertService
      .showConfirmation(
        'warning',
        'building.resetMeasures.default.confirmation.title',
        'building.resetMeasures.default.confirmation.text'
      )
      .subscribe({
        next: (result: AlertModalResult) => {
          if (result !== AlertModalResult.CONFIRM) {
            return;
          }

          this.loading.set(true);
          this.buildingsService.v2BuildingsBuildingIdMeasuresResetDefaultPut(buildingId).subscribe({
            next: () => {
              this.sharedService.emitDeleteLocalCache(DeleteLocalCacheCategory.All);
              this.sharedService.emitReloadComponent();
              this.alertService.showAlert('success', 'building.resetMeasures.default.success.title');
            },
            error: () => {
              this.loading.set(false);
              this.alertService.showAlert('error', 'error.general');
            }
          });
        }
      });
  }

  protected getEnergyLabelTranslationKey(key: string): string {
    return `${this.energyLabelTranslationPrefix()}${key}`;
  }

  protected hasEnergyLabelTranslation(key: string): boolean {
    return this.translateService.instant(this.getEnergyLabelTranslationKey(key)) !== '';
  }

  public getExportSheet(addDescription: boolean): ExcelExportSheet {
    return this.buildingMeasuresOverviewExcelExportService.getExportSheet(
      this.columns(),
      this.filteredBuildingMeasures() || [],
      this.sortBy(),
      this.sortOrder(),
      !!this.licenses().vat,
      this.labelGroup(),
      this.showM2(),
      addDescription
    );
  }

  protected export(): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }
    this.buildingMeasuresOverviewExcelExportService.export(
      buildingId,
      this.columns(),
      this.filteredBuildingMeasures() || [],
      this.sortBy(),
      this.sortOrder(),
      !!this.licenses().vat,
      this.labelGroup(),
      this.showM2(),
      false
    );
  }

  protected setSortBy(sortBy: string | string[] | undefined): void {
    this.sortBy.set(sortBy);

    const objective = this.objective();
    if (objective) {
      const objectiveSortBy = this.getObjectiveSortBy(objective);
      if (JSON.stringify(objectiveSortBy) !== JSON.stringify(sortBy)) {
        this.objective.set(undefined);
      }
    }
  }

  protected setSortOrder(sortOrder: DataTableSortOrder | DataTableSortOrder[] | undefined): void {
    this.sortOrder.set(sortOrder);

    const objective = this.objective();
    if (objective) {
      const objectiveSortOrder = this.getObjectiveSortOrder(objective);
      if (JSON.stringify(objectiveSortOrder) !== JSON.stringify(sortOrder)) {
        this.objective.set(undefined);
      }
    }
  }

  protected showDetails(measure: BuildingMeasuresOverviewMeasure, index: number, event: MouseEvent): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.goToMeasure(measure, index, event.target as HTMLButtonElement);
  }

  private goToMeasure(
    measure: BuildingMeasuresOverviewMeasure,
    index: number,
    buttonElememt?: HTMLButtonElement | null
  ): void {
    if (measure.buildingMeasureType === BuildingMeasureTypeDTO.CALCULATED) {
      if (this.environment.useSidePanelMeasureDetails) {
        this.showMeasureSidePanel(index, buttonElememt);
      } else {
        this.router
          .navigateByUrl(
            `/${this.module()}/${this.portfolioId()}/buildings/${this.buildingId()}/measures/${measure.id}`
          )
          .then(() => {
            window.scrollTo(0, 0);
          });
      }
    } else {
      this.addOrEditUserDefiniedBuildingMeasure(measure);
    }
  }

  private showMeasureSidePanel(index: number, buttonElememt?: HTMLButtonElement | null): void {
    const modalRef = this.modalService.show(
      BuildingMeasureSidePanel,
      {
        initialState: {
          portfolioId: this.portfolioId(),
          buildingId: this.buildingId(),
          buildingMeasureIndex: index,
          measureParametersWithInfo: this.measureParametersWithInfo(),
          measureParametersToHide: this.measureParametersToHide(),
          showHeaderLink: this.showMeasureDetailsButtonSidePanel(),
          buildingMeasureIds: this.dataTable()
            ?.pagedData()
            ?.map((measure: BuildingMeasuresOverviewMeasure) => {
              return measure.id;
            }),
          showPaybackPeriodInYearsOnly: this.showPaybackPeriodInYearsOnly()
        },
        class: 'modal-dialog-scrollable modal-dialog-slideout modal-sm',
        ignoreBackdropClick: true,
        keyboard: false,
        previousActiveElement: buttonElememt ?? undefined
      },
      true
    );

    if (modalRef.content) {
      modalRef.content.openInfoModal?.subscribe((measureCode: MeasureCodeDTO) => {
        this.openInfoModal.emit(measureCode);
      });
    }
  }

  private addOrEditUserDefiniedBuildingMeasure(measure?: BuildingMeasuresOverviewMeasure): void {
    const modelRef = this.modalService.showSidePanel(MeasureUpdateModal, {
      initialState: {
        buildingId: this.buildingId(),
        building: this.building(),
        buildingMeasureId: measure?.id,
        buildingCurrencyCode: measure?.currencyCode,
        heatingConsumptionType: this.heatingConsumptionType(),
        showCloseButton: false
      }
    });

    if (modelRef.content) {
      modelRef.content.onClose.subscribe((reload: boolean): void => {
        if (reload) {
          this.getMeasures();
        }
      });
    }
  }

  private getMeasures(isLoading: boolean = true): void {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }
    this.loading.set(isLoading);
    this.hasError.set(false);
    this.buildingMeasuresListService
      .v2BuildingsIdBuildingmeasuresPost(
        { ...this.filtersBuildingSearchService.searchFilters(), ...{ amountsPerM2: this.showM2() } },
        buildingId
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: MeasureListDTO) => {
          this.heatingConsumptionType.set(response.heatingConsumptionType);
          this.currencyCode.set(response.currencyCode);
          this.totalFilteredMeasures.set(response.measureList.length);
          this.initMeasureList(response);
        },
        error: () => {
          this.loading.set(false);
          this.initialLoading.set(false);
          this.hasError.set(true);
        }
      });
  }

  private initMeasureList(measureList: MeasureListDTO): void {
    let labelGroups = measureList.measureList
      .map((measure) => this.labelGroupService.getLabelGroups(measure.impactEnergyLabels))
      .reduce((acc, val) => acc.concat(val), []);

    if (!this.labelGroup()) {
      this.labelGroup.set(this.labelGroupService.getDefaultLabelGroup(labelGroups));
    }
    this.mainLabelGroup.set(this.labelGroupService.getMainLabelGroup(labelGroups));

    this.buildingMeasures.set(
      measureList.measureList.map((buildingMeasureTableItem: BuildingMeasureTableItemDTO) => {
        return this.mapToBuildingMeasuresOverviewMeasure(buildingMeasureTableItem);
      })
    );

    this.updateFilteredMeasures();
    this.currencyCode.set(measureList.currencyCode);
    this.heatingConsumptionType.set(measureList.heatingConsumptionType);
    this.loading.set(false);
    this.initialLoading.set(false);
  }

  private getObjectiveSortOrder(
    objective: BuildingMeasuresOverviewObjective
  ): DataTableSortOrder | DataTableSortOrder[] {
    switch (objective) {
      case BuildingMeasuresOverviewObjective.CostReduction:
        return DataTableSortOrder.Descending;
      case BuildingMeasuresOverviewObjective.Co2Reduction:
        return [DataTableSortOrder.Descending, DataTableSortOrder.Descending];
      case BuildingMeasuresOverviewObjective.ImproveEnergyLabel:
        return DataTableSortOrder.Descending;
      case BuildingMeasuresOverviewObjective.ComplyLawRegulations:
        return [DataTableSortOrder.Descending, DataTableSortOrder.Descending];
      case BuildingMeasuresOverviewObjective.ImplementationOrder:
        return [DataTableSortOrder.Ascending, DataTableSortOrder.Descending, DataTableSortOrder.Descending];
    }
  }

  private getObjectiveSortBy(objective: BuildingMeasuresOverviewObjective): string | string[] {
    switch (objective) {
      case BuildingMeasuresOverviewObjective.CostReduction:
        return 'costReduction';
      case BuildingMeasuresOverviewObjective.Co2Reduction:
        return ['co2ConsumptionReduction', 'impactEnergyLabel'];
      case BuildingMeasuresOverviewObjective.ImproveEnergyLabel:
        const labelGroup = this.labelGroup();
        if (labelGroup === LabelGroupDTO.CARBONSCORE && labelGroup?.length > 1) {
          // This is a hack to prevent showing a sorting icon the impactEnergyLabel column in case
          // the objective improve label is selected, but the carbon label is shown instead of the main label
          return ['impactEnergyLabel', 'fakeImpactEnergyLabelMainLabel'];
        }
        return 'impactEnergyLabel';
      case BuildingMeasuresOverviewObjective.ComplyLawRegulations:
        return ['recognized', 'impactEnergyLabel'];
      case BuildingMeasuresOverviewObjective.ImplementationOrder:
        return ['implementationOrder', 'co2ConsumptionReduction', 'impactEnergyLabel'];
    }
  }

  private updateFilteredMeasures(): void {
    if (!this.keepCurrentSorting) {
      const defaultSortBy: string | string[] | undefined =
        this.environment.components.buildingMeasuresOverview?.defaultSortBy;
      const defaultSortOrder: DataTableSortOrder | DataTableSortOrder[] | undefined = this.environment.components
        .buildingMeasuresOverview?.defaultSortOrder as DataTableSortOrder | DataTableSortOrder[] | undefined;
      this.sortBy.set(defaultSortBy || 'impactEnergyLabel');
      this.sortOrder.set(defaultSortOrder || DataTableSortOrder.Descending);

      const objective = this.objective();
      if (this.activeScenario() === 'fiveYear') {
        this.sortBy.set('paybackPeriodYears');
        this.sortOrder.set(DataTableSortOrder.Ascending);
      } else if (objective) {
        this.sortBy.set(this.getObjectiveSortBy(objective));
        this.sortOrder.set(this.getObjectiveSortOrder(objective));
      } else {
        if (this.activeScenario() === 'allMeasures' && this.environment.components.customerMeasures) {
          this.sortBy.set(['isCustomerMeasure', 'impactEnergyLabel']);
          this.sortOrder.set([DataTableSortOrder.Descending, DataTableSortOrder.Descending]);
        }
      }
    }

    this.keepCurrentSorting = false;

    this.updateFilteredMeasuresResult();

    queueMicrotask(() => this.measureCount.emit(this.filteredBuildingMeasures()?.length || 0));
  }

  private updateFilteredMeasuresResult(): void {
    let filtered = this.buildingMeasuresService.filterBuildingMeasures(this.buildingMeasures(), this.searchText());

    filtered = this.buildingMeasuresService.filterActiveScenarios(this.activeScenario(), filtered);

    if (this.hideNonCostReducingMeasures()) {
      filtered = filtered?.filter((measure) => {
        if (this.visibleNonCostReducingMeasureCodes()?.includes(measure.code as MeasureCodeDTO)) {
          return true;
        }
        if (measure.userDefinedCostReduction !== null) {
          return true;
        }
        return measure.costReduction > 0;
      });
    }

    this.filteredBuildingMeasures.set(filtered);
  }

  private mapToBuildingMeasuresOverviewMeasure(
    buildingMeasureTableItem: BuildingMeasureTableItemDTO
  ): BuildingMeasuresOverviewMeasure {
    let measure = buildingMeasureTableItem as BuildingMeasuresOverviewMeasure;

    if (measure.buildingMeasureType === BuildingMeasureTypeDTO.CALCULATED) {
      measure.name = this.translateService.instant('measure.name.' + measure.code);
    }

    if (this.environment.components.customerMeasures && measure.code) {
      measure.isCustomerMeasure = this.environment.components.customerMeasures.includes(measure.code);
    }

    if (measure.co2ConsumptionReduction > 1) {
      // CO2 above 1 ton is display as whole number, for sorting it should be rounded
      // TODO: Should we move this to the backend?
      measure.co2ConsumptionReduction = Math.round(measure.co2ConsumptionReduction);
    }

    measure.searchable = measure.name?.toLowerCase() ?? '';
    measure.isCustomerMeasure = this.isCustomerMeasure(measure);
    measure.icon = this.getMeasureIcon(measure);
    measure.iconTitle = this.getMeasureIconTitle(measure);
    // TODO: setting implementationOrder can be removed when it is part of BuildingMeasureTableItemDTO
    measure.implementationOrder = IMPLEMENTATION_ORDER.indexOf(measure.code as MeasureCodeDTO);
    return measure;
  }

  private getMeasureIcon(measure: BuildingMeasuresOverviewMeasure): string {
    if (!this.showIcon()) {
      return '';
    }

    let iconClass: string | undefined;
    switch (true) {
      case measure.isCustomerMeasure:
        iconClass = this.environment.icons.customerMeasureIcon;
        break;
      case this.environment.components.showRecognisedMeasureCheck && measure.recognized:
        iconClass = this.environment.icons.iconRecognizedMeasure;
        break;
      case this.showSubsidyIcon() && measure.subsidyAvailable:
        iconClass = this.environment.icons.iconSubsidy;
        break;
      case this.environment.icons.iconUserDefinedMeasure &&
        measure.buildingMeasureType === BuildingMeasureTypeDTO.USERDEFINED:
        iconClass = this.environment.icons.iconUserDefinedMeasure;
        break;
    }

    return iconClass ? `${iconClass} ms-2` : '';
  }

  private isCustomerMeasure(measure: BuildingMeasuresOverviewMeasure): boolean {
    return (
      !!measure.code &&
      !!this.environment.components.customerMeasures &&
      this.environment.components.customerMeasures.includes(measure.code)
    );
  }

  private shouldShowCustomerMeasureIcon(measure: BuildingMeasuresOverviewMeasure): boolean {
    return !!this.environment.icons.customerMeasureIcon && measure.isCustomerMeasure === true;
  }

  private onPaybackPeriodYearsCompare(
    a: BuildingMeasuresOverviewMeasure,
    b: BuildingMeasuresOverviewMeasure,
    sortOrder: DataTableSortOrder | DataTableSortOrder[]
  ): number {
    // Make sure the measure are sorted at the end as highest payback in case that:
    // 1. The cost reduction is negative and the investment is not able to be payed back
    // 2. Both cost reduction en investment are zero and payback period is not applicable
    const aToBottom = a.costReduction <= 0 || (a.costReduction === 0 && a.investment === 0);
    const bToBottom = b.costReduction <= 0 || (b.costReduction === 0 && b.investment === 0);

    switch (true) {
      case aToBottom && !bToBottom:
        // A should be sorted to the bottom
        return sortOrder === DataTableSortOrder.Ascending ? 1 : -1;
      case !aToBottom && bToBottom:
        // B should be sorted to the bottom
        return sortOrder === DataTableSortOrder.Ascending ? -1 : 1;
      case aToBottom && bToBottom:
        if (a.investment < b.investment) {
          return sortOrder === DataTableSortOrder.Ascending ? -1 : 1;
        } else if (a.investment > b.investment) {
          // Both should be sorted to the bottom, use investment for order
          return sortOrder === DataTableSortOrder.Ascending ? 1 : -1;
        }

        return 0;
      case a.paybackPeriodYears !== undefined &&
        b.paybackPeriodYears !== undefined &&
        a.paybackPeriodYears < b.paybackPeriodYears:
        return sortOrder === DataTableSortOrder.Ascending ? -1 : 1;
      case a.paybackPeriodYears !== undefined &&
        b.paybackPeriodYears !== undefined &&
        a.paybackPeriodYears > b.paybackPeriodYears:
        return sortOrder === DataTableSortOrder.Ascending ? 1 : -1;
    }

    return 0;
  }

  private getEnergyConsumptionReduction(measure: BuildingMeasuresOverviewMeasure): number {
    if (measure.electricityConsumptionReductionInKwh && measure.electricityConsumptionReductionInKwh > 0) {
      return measure.electricityConsumptionReductionInKwh;
    } else if (measure.gasConsumptionReductionInM3 && measure.gasConsumptionReductionInM3 > 0) {
      return measure.gasConsumptionReductionInM3;
    } else if (measure.heatConsumptionReductionInGJ && measure.heatConsumptionReductionInGJ > 0) {
      return measure.heatConsumptionReductionInGJ;
    }
    return 0;
  }

  private onImpactEnergyLabelCompare(
    a: BuildingMeasuresOverviewMeasure,
    b: BuildingMeasuresOverviewMeasure,
    sortOrder: DataTableSortOrder | DataTableSortOrder[]
  ): number {
    return this.buildingMeasuresService.impactEnergyLabelCompare(
      a,
      b,
      sortOrder,
      this.objective(),
      this.labelGroup(),
      this.labelGroups()
    );
  }

  private onEnergyConsumptionCompare(
    a: BuildingMeasuresOverviewMeasure,
    b: BuildingMeasuresOverviewMeasure,
    sortOrder: DataTableSortOrder | DataTableSortOrder[]
  ): number {
    const aEnergyConsumptionReduction = this.getEnergyConsumptionReduction(a);
    const bEnergyConsumptionReduction = this.getEnergyConsumptionReduction(b);

    if (aEnergyConsumptionReduction < bEnergyConsumptionReduction) {
      return sortOrder === DataTableSortOrder.Ascending ? -1 : 1;
    } else if (aEnergyConsumptionReduction > bEnergyConsumptionReduction) {
      return sortOrder === DataTableSortOrder.Ascending ? 1 : -1;
    }

    return 0;
  }

  private getMeasureIconTitle(measure: BuildingMeasuresOverviewMeasure): string {
    const hasUserDefinedIcons: boolean =
      !!this.environment.icons.iconUserDefinedMeasure &&
      measure.buildingMeasureType === BuildingMeasureTypeDTO.USERDEFINED;

    const canShowRecognisedMeasure: boolean =
      !!this.environment.components.showRecognisedMeasureCheck && measure.recognized === true;

    if (this.shouldShowCustomerMeasureIcon(measure)) {
      return 'measure.customer';
    } else if (canShowRecognisedMeasure) {
      return 'measure.recognized';
    } else if (this.showSubsidyIcon() && measure.subsidyAvailable) {
      return 'measure.subsidized';
    } else if (hasUserDefinedIcons) {
      return 'measure.userDefined';
    }

    return '';
  }

  protected async showBudgetingModal(): Promise<void> {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return;
    }

    const { BudgetModal } = await import('@cfp/features-shared/budget');

    const budgetComponentRef = this.modalService.show(BudgetModal, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-sm'
    });

    if (budgetComponentRef.content) {
      budgetComponentRef.content.onSave.subscribe((amount: number) => {
        this.buildingsService.v2BuildingsBuildingIdBudgetPlanPut({ amount }, buildingId).subscribe({
          next: () => {
            this.alertService.showAlert('success', 'saved', 'changed_saved').subscribe(() => this.showFeedbackModal());
            budgetComponentRef.hide();

            this.sharedService.emitReloadComponent();
            this.changeScenario('planned');
          },
          error: () => {
            budgetComponentRef.hide();
            this.alertService.showAlert('error', 'error.general');
          }
        });
      });

      budgetComponentRef.content.openLink.subscribe(() => this.showBudgetInfo());
    }
  }

  protected async showBudgetInfo(): Promise<void> {
    const { BudgetInfoSidePanel } = await import('@cfp/features-shared/budget');

    this.showModalRef(BudgetInfoSidePanel);
  }

  protected async showFeedbackModal(): Promise<void> {
    const { BudgetPopupInfoModal } = await import('@cfp/features-shared/budget');

    const infoModalRef = this.modalService.show(BudgetPopupInfoModal, {
      class: 'modal-sm modal-dialog-centered'
    });

    if (infoModalRef.content) {
      infoModalRef.content.openLink.subscribe(() => {
        infoModalRef.hide();
        this.showModalRef(BudgetPopupInfoModal);
      });
    }
  }

  private showModalRef<T>(component, classes?: string): BsModalRef<T> {
    return this.modalService.show<T>(component, {
      class: `modal-dialog-scrollable modal-dialog-slideout modal-sm ${classes || ''}`
    });
  }

  private setMeasureList(measureList: MeasureListDTO): void {
    this.filteredBuildingMeasures.set(measureList.measureList as BuildingMeasuresOverviewMeasure[]);
    this.updateFilteredMeasuresResult();
    this.initMeasureList(measureList);
    this.filtersActive.set(this.filtersBuildingSearchService.hasSearchFilters());
    this.updateLiveRegionItemsShowing();
    this.filterBadges.set(this.buildingFilterBadgeService.filterBadges);
    this.currencyCode.set(measureList.currencyCode);
    this.totalFilteredMeasures.set(measureList.measureList.length);

    const modalRefContent = this.buildingMeasuresFiltersSidePanelModalRef?.content;
    if (modalRefContent) {
      modalRefContent.totalFilteredMeasures.set(measureList.measureList.length);
      modalRefContent.isLoading.set(false);
    }
  }

  private getBuildingMeasures(): Observable<MeasureListDTO | undefined> {
    const buildingId = this.buildingId();
    if (!buildingId) {
      return of(undefined);
    }

    return this.buildingMeasuresListService
      .v2BuildingsIdBuildingmeasuresFacetsPost(
        { ...this.filtersBuildingSearchService.searchFilters(), ...{ amountsPerM2: this.showM2() } },
        buildingId
      )
      .pipe(
        tap((buildingMeasureSearchFacets: BuildingMeasureSearchFacetsDTO) => {
          this.filtersBuildingSearchService.searchFacets.set(buildingMeasureSearchFacets);

          const modalRefContent = this.buildingMeasuresFiltersSidePanelModalRef?.content;
          if (modalRefContent) {
            modalRefContent.ngOnInit();
          }
        }),
        mergeMap(() =>
          this.buildingMeasuresListService.v2BuildingsIdBuildingmeasuresPost(
            { ...this.filtersBuildingSearchService.searchFilters(), ...{ amountsPerM2: this.showM2() } },
            buildingId
          )
        ),
        tap((measureList: MeasureListDTO) => {
          this.setMeasureList(measureList);
        })
      );
  }

  private resetFiltersAndBadges(): void {
    this.filtersBuildingSearchService.resetSearchFilters();
    this.buildingFilterBadgeService.filterBadges = [];
  }
}
